import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Space,
  Table,
  Modal,
  Upload,
  message,
  Tag,
  Switch,
  AutoComplete,
  Radio,
  Tooltip,
} from "antd";
import {
  PlusOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, EditOutlined, SearchOutlined, SendOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useList, useCustom } from "@refinedev/core";
import { TEAM_LIST_QUERY } from "../../teams/queries";
import { REQUEST_TEMPLATE_LIST_QUERY } from "../../purchase-template/queries";


import { API_URL } from "../../../helpers/gqlProvider";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { MY_ORGANIZATION_QUERY } from "../../organizations/queries";
import { Organization } from "../../organizations/interface";
import generalCategoriesData from "../../procurement-plan/data/categories.json";
import clinicalCategoriesData from "../../procurement-plan/data/categories-clinical.json";

export type Attachment = {
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
};

type ItemRow = {
  id?: string;
  name: string;
  description?: string;
  quantity: number;
  unit?: string;
  categoryType?: string;
  productCategory?: string;
  unitPrice: number;
  totalPrice: number;
  supplier?: string;
  planRef?: { planId?: string; itemId?: string };
  priority?: string;
  justification?: string;
  tags?: string[];
};

type Props = {
  form: any;
  isEdit?: boolean;
  requestId?: string;
  initialValues?: any;
  // actions
  onSaveDraft: (values: any) => Promise<void> | void;
  onSubmit: (payload: { requestId?: string; requestData?: any; fiscalYear: number }) => Promise<void> | void;
};

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL 
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;
const filePreviewUrl = (fileId: string) => `${deriveFileApiBase()}/file-storage/preview/${fileId}`;

export const PurchaseRequestForm: React.FC<Props> = ({
  form,
  isEdit = false,
  requestId,
  initialValues = {},
  onSaveDraft,
  onSubmit,
}) => {
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<ItemRow[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [planSearch, setPlanSearch] = useState<string>("");
  const [approvedItems, setApprovedItems] = useState<any[]>([]);
  const [approvedLoading, setApprovedLoading] = useState<boolean>(false);
  const [isUnplanned, setIsUnplanned] = useState<boolean>(false);

  // Track if title/description have been manually edited
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [descriptionManuallyEdited, setDescriptionManuallyEdited] = useState(false);

  // Watch form fields for auto-generation
  const teamId = Form.useWatch('teamId', { form });
  const fiscalYear = Form.useWatch('fiscalYear', { form });
  const dateRequired = Form.useWatch('dateRequired', { form });

  // Watch attachments from form
  const attachments = Form.useWatch("attachments", { form, preserve: true }) || [];

  const getMimeTypeIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return <FilePdfOutlined />;
    } else if (mimeType.includes("image")) {
      return <FileImageOutlined />;
    } else if (mimeType.includes("text")) {
      return <FileTextOutlined />;
    }
    return <FileOutlined />;
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Teams for teamId select
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams = (teamsData?.data && !Array.isArray(teamsData.data)) ? (teamsData.data as any).teams?.nodes || [] : teamsData?.data || [];

  const CATEGORY_TYPE_OPTIONS: { value: string; label: string; description: string }[] = [
    { value: 'GOODS', label: 'Goods', description: 'Physical, tangible items (e.g., office supplies, laptops)' },
    { value: 'SERVICES', label: 'Services', description: 'Intangible activities or expertise (e.g., consulting, software maintenance)' },
    { value: 'WORKS', label: 'Works', description: 'Construction, engineering, and infrastructure projects (e.g., building a bridge, road repairs)' },
  ];

  const { data: orgData } = useCustom<{ myOrganization: Organization | null }>({
    url: "",
    method: "get",
    meta: { gqlQuery: MY_ORGANIZATION_QUERY },
  });
  const industry = orgData?.data?.myOrganization?.serviceIndustry?.toLowerCase();
  const categoriesData = industry?.includes('clinical') ? clinicalCategoriesData : generalCategoriesData;

  const itemCategoryType = Form.useWatch('categoryType', { form: itemForm }) as string | undefined;
  const planRefValue = Form.useWatch('planRef', { form: itemForm });

  // Auto-generate title and description based on team, fiscal year, and date required
  useEffect(() => {
    if (teamId && fiscalYear) {
      // Find the selected team
      const selectedTeam = teams.find((t: any) => t.id === teamId);
      const teamName = selectedTeam?.name || "Unknown Team";

      // Generate title
      const generatedTitle = `Purchase Request - ${teamName} (FY ${fiscalYear})`;

      // Only auto-fill if fields haven't been manually edited
      if (!titleManuallyEdited) {
        form.setFieldValue("title", generatedTitle);
      }

      // Generate description if dateRequired is also set
      if (dateRequired) {
        const dateRequiredStr = dateRequired.format ? dateRequired.format('MMMM DD, YYYY') :
                               new Date(dateRequired).toLocaleDateString('en-US', {
                                 year: 'numeric',
                                 month: 'long',
                                 day: 'numeric'
                               });

        const generatedDescription = `Purchase request for ${teamName} for fiscal year ${fiscalYear}. Items are required by ${dateRequiredStr}.`;

        if (!descriptionManuallyEdited) {
          form.setFieldValue("description", generatedDescription);
        }
      }
    }
  }, [teamId, fiscalYear, dateRequired, teams, titleManuallyEdited, descriptionManuallyEdited, form]);

  // Purchase Templates
  const { data: templatesData, isLoading: templatesLoading } = useList<any>({
    resource: "purchaseTemplates", // Changed from "purchase-templates"
    meta: { gqlQuery: REQUEST_TEMPLATE_LIST_QUERY },
  });
  const templates = ((templatesData?.data && !Array.isArray(templatesData.data)) ? (templatesData.data as any).purchaseTemplates?.nodes || [] : templatesData?.data || [])
    .filter((t: any) => t.templateKind === 'REQUEST');

  // No longer using separate attach/remove mutations

  // Initialize form defaults
  useEffect(() => {
    // Set date requested to today by default
    const today = dayjs();
    console.log({initialValues})
    form.setFieldsValue({
      dateRequested: initialValues?.dateRequested ? dayjs(initialValues.dateRequested) : today,
      dateRequired: initialValues?.dateRequired ? dayjs(initialValues.dateRequired) : undefined,
      title: initialValues?.title,
      description: initialValues?.description,
      isUnplanned: initialValues?.isUnplanned || false,
      ...initialValues,
    });
    // Set local state based on initial values
    setIsUnplanned(initialValues?.isUnplanned || false);
    if (initialValues?.items?.length) {
      setItems(
        (initialValues.items as any[]).map((it) => ({
          id: it.id,
          name: it.name,
          description: it.description,
          quantity: it.quantity,
          unit: it.unit,
          categoryType: it.categoryType,
          productCategory: it.productCategory,
          unitPrice: it.unitPrice,
          totalPrice: it.totalPrice,
          supplier: it.supplier,
          planRef: it.planRef,
          priority: it.priority,
          justification: it.justification,
          tags: it.tags,
        })),
      );
    }
  }, [JSON.stringify(initialValues), form]);

  useEffect(() => {
    if (isEdit && initialValues) {
      if (initialValues.templateId) {
        form.setFieldsValue({ templateId: initialValues.templateId });
      }
      // Set isUnplanned state based on initial values in edit mode
      if (initialValues.isUnplanned !== undefined) {
        setIsUnplanned(initialValues.isUnplanned);
        form.setFieldValue('isUnplanned', initialValues.isUnplanned);
      }
    }
  }, [isEdit, initialValues, form]);

  // Auto-select first available request template for new requests
  useEffect(() => {
    if (!isEdit && templates.length > 0 && !form.getFieldValue('templateId')) {
      const firstTemplate = templates[0];
      form.setFieldsValue({ templateId: firstTemplate.id });
      // Apply template settings
      form.setFieldValue("teamId", firstTemplate.teamId);
      // Only set fiscal year if provided in template, otherwise keep default
      if (firstTemplate.fiscalYear) {
        form.setFieldValue("fiscalYear", firstTemplate.fiscalYear);
      }
      // Copy approval workflow from template
      form.setFieldValue("steps", firstTemplate.steps);
      form.setFieldValue("approvalType", firstTemplate.approvalType);
      form.setFieldValue("externalWorkflow", firstTemplate.externalWorkflow);
      message.success(`Template '${firstTemplate.name}' applied by default.`);
    }
  }, [isEdit, templates, form]);


  // // Ensure template is selected when templates load
  // useEffect(() => {
  //   if (initialValues?.templateId && templates.length > 0 && !templatesLoading) {
  //     form.setFieldsValue({ templateId: initialValues.templateId });
  //   }
  // }, [templates, initialValues, form, templatesLoading]);

  // Totals
  const totalAmount = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.totalPrice || 0), 0),
    [items],
  );

  // Approved plan items autocomplete search
  const runApprovedSearch = useCallback(async () => {
    const teamId = form.getFieldValue("teamId");
    const fiscalYear = form.getFieldValue("fiscalYear");
    if (!teamId || !fiscalYear) {
      setApprovedItems([]);
      return;
    }
    setApprovedLoading(true);
    try {
      const body = {
        query: `
          query ApprovedPlanItems($teamId: String!, $fiscalYear: Float!, $search: String, $limit: Float) {
            approvedPlanItems(teamId: $teamId, fiscalYear: $fiscalYear, search: $search, limit: $limit) {
              itemId
              planId
              productName
              productDescription
              unitPrice
              quantity
              supplier
            }
          }
        `,
        variables: {
          teamId,
          fiscalYear: Number(fiscalYear),
          search: planSearch || undefined,
          limit: 10,
        },
      };
      const resp = await fetch(API_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      setApprovedItems(json?.data?.approvedPlanItems || []);
    } catch (e) {
      console.error("approvedPlanItems error", e);
    } finally {
      setApprovedLoading(false);
    }
  }, [form, planSearch]);

  useEffect(() => {
    // re-search when team/fiscalYear changes
    const teamId = form.getFieldValue("teamId");
    const fy = form.getFieldValue("fiscalYear");
    if (teamId && fy) {
      runApprovedSearch();
    }
   
  }, [form.getFieldValue("teamId"), form.getFieldValue("fiscalYear")]);

  // Add or update item
  const saveItem = async () => {
    try {
      const v = await itemForm.validateFields();
      const row: ItemRow = {
        name: v.name,
        description: v.description,
        quantity: Number(v.quantity),
        unit: v.unit,
        categoryType: v.categoryType,
        productCategory: v.category,
        unitPrice: Number(v.unitPrice),
        totalPrice: Number(v.quantity) * Number(v.unitPrice),
        supplier: v.supplier,
        planRef: v.planRef,
        priority: v.priority,
        justification: v.justification,
        tags: v.tags || [],
      };
      if (editingIndex !== null && editingIndex >= 0) {
        const copy = [...items];
        copy[editingIndex] = { ...copy[editingIndex], ...row };
        setItems(copy);
      } else {
        setItems((prev) => [...prev, { ...row, id: `tmp-${Date.now()}` }]);
      }
      setIsItemModalVisible(false);
      itemForm.resetFields();
      setEditingIndex(null);
    } catch (e) {
      // validation errors shown by antd
    }
  };

  const editItem = (index: number) => {
    const it = items[index];
    itemForm.setFieldsValue({
      name: it.name,
      description: it.description,
      categoryType: it.categoryType || 'GOODS',
      category: it.productCategory,
      quantity: it.quantity,
      unit: it.unit,
      unitPrice: it.unitPrice,
      supplier: it.supplier,
      planRef: it.planRef,
      priority: it.priority,
      justification: it.justification,
      tags: it.tags || [],
    });
    setEditingIndex(index);
    setIsItemModalVisible(true);
  };

  const removeItem = (index: number) => {
    const copy = [...items];
    copy.splice(index, 1);
    setItems(copy);
  };

  // Build payload for save/update
  const buildPayload = () => {
    const fv = form.getFieldsValue();
    const payload = {
      title: fv.title,
      description: fv.description,
      isUnplanned: fv.isUnplanned,
      templateId: fv.templateId, // Add templateId to payload
      teamId: fv.teamId,
      dateRequested: fv.dateRequested ? fv.dateRequested.toDate?.() ?? fv.dateRequested : new Date(),
      dateRequired: fv.dateRequired ? fv.dateRequired.toDate?.() ?? fv.dateRequired : new Date(),
      requestedBy: fv.requestedBy,
      budgetId: fv.budgetId,
      items: items.map((it) => ({
        name: it.name,
        description: it.description,
        quantity: it.quantity,
        unit: it.unit,
        categoryType: it.categoryType,
        productCategory: it.productCategory,
        unitPrice: it.unitPrice,
        totalPrice: it.totalPrice,
        supplier: it.supplier,
        planRef: it.planRef,
        priority: it.priority,
        justification: it.justification,
        tags: it.tags,
      })),
      attachments: fv.attachments || [], // Get attachments from form values
      tags: fv.tags,
      notes: fv.notes,
      approvals: fv.approvals, // Include approvals from template
    };
    return payload;
  };

  // Handlers
  const handleSaveDraft = async () => {
    await form.validateFields();
    await onSaveDraft(buildPayload());
  };

  const handleSubmit = async () => {
    await form.validateFields();
    const fiscalYear = form.getFieldValue("fiscalYear");
    if (!fiscalYear) {
      message.error("Fiscal Year is required for submission");
      return;
    }
    const payload = buildPayload();
    console.log("Submitting Purchase Request with payload:", payload);
    if (requestId) {
      await onSubmit({ requestId, fiscalYear: Number(fiscalYear) });
    } else {
      await onSubmit({ requestData: payload, fiscalYear: Number(fiscalYear) });
    }
  };

  // File upload - now uses form values instead of separate state
  const uploadProps = {
    name: "file",
    multiple: true,
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        // Update form attachments field - only store necessary data
        const currentAttachments = form.getFieldValue("attachments") || [];
        const attachmentData: Attachment = {
          fileId: fileData.fileId,
          originalName: fileData.originalName,
          mimeType: fileData.mimeType,
          size: fileData.size,
        };
        const next = [...currentAttachments, attachmentData];
        form.setFieldValue("attachments", next);
        onSuccess?.(json, file);
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
  };

  const handleRemoveAttachment = (fileIdToRemove: string) => {
    const currentAttachments = attachments || [];
    const next = currentAttachments.filter((a: Attachment) => a.fileId !== fileIdToRemove);
    form.setFieldValue("attachments", next);
    message.success("Attachment removed locally");
  };

  // Columns
  const columns: any[] = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Type", dataIndex: "categoryType", render: (v: string) => CATEGORY_TYPE_OPTIONS.find(o => o.value === v)?.label || v },
    { title: "Category", dataIndex: "productCategory" },
    { title: "Qty", dataIndex: "quantity" },
    { title: "Unit", dataIndex: "unit" },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}`,
    },
    { title: "Supplier", dataIndex: "supplier" },
    { title: "Priority", dataIndex: "priority", render: (v: string) => v ? <Tag>{v}</Tag> : null },
    {
      title: "From Plan",
      render: (_: any, r: ItemRow) =>
        r.planRef?.planId ? (
          <Tag color="blue">{r.planRef.planId} • {r.planRef.itemId}</Tag>
        ) : (
          <Tag>Manual</Tag>
        ),
    },
    {
      title: "Actions",
      render: (_: any, _r: ItemRow, i: number) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => editItem(i)} />
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => removeItem(i)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          budgetSource: "OPS",
          fiscalYear: new Date().getFullYear(),
          ...initialValues,
        }}
      >
        <Card 
          title={
            <Space>
              <span>Request Information</span>
              <span style={{ fontSize: '12px', color: '#666' }}>Unplanned</span>
              <Switch
                size="small"
                checked={isUnplanned}
                onChange={(checked) => {
                  setIsUnplanned(checked);
                  form.setFieldValue('isUnplanned', checked);
                }}
              />
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
      
            <Col span={24}>
              <Form.Item
                name="templateId"
                label="Purchase Template"
              >
                <Select
                  placeholder="Select a template to pre-fill fields"
                  disabled={templatesLoading}
                      loading={templatesLoading}
                  allowClear
                
                  onChange={(value) => {
                    if (value) {
                      const selectedTemplate = templates.find((t: any) => t.id === value);
                      if (selectedTemplate) {
                        form.setFieldValue("teamId", selectedTemplate.teamId);
                        form.setFieldValue("fiscalYear", selectedTemplate.fiscalYear);
                        // Copy approval workflow from template
                        form.setFieldValue("approvals", selectedTemplate.steps);
                        form.setFieldValue("approvalType", selectedTemplate.approvalType);
                        form.setFieldValue("externalWorkflow", selectedTemplate.externalWorkflow);
                        message.success(`Template '${selectedTemplate.name}' applied.`);
                      }
                    } else {
                      // Clear template-related fields if template is unselected
                      form.setFieldValue("teamId", undefined);
                      form.setFieldValue("fiscalYear", undefined);
                      form.setFieldValue("approvals", undefined);
                      form.setFieldValue("approvalType", undefined);
                      form.setFieldValue("externalWorkflow", undefined);
                    }
                  }}
                >
                  {(templates as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name} ({t.fiscalYear})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="teamId"
                label="Team"
                rules={[{ required: true, message: "Please select a team" }]}
              >
                <Select placeholder="Select team" loading={teamsLoading} disabled={teamsLoading || teamsError}>
                  {(teams as any[]).map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="dateRequested"
                label="Date Requested"
                rules={[{ required: true, message: "Please pick date requested" }]}
              >
                <DatePicker style={{ width: "100%" }} disabled />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="dateRequired"
                label="Date Required"
                rules={[{ required: true, message: "Please pick date required" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="fiscalYear"
                label="Fiscal Year (for Submission)"
                rules={[{ required: true, message: "Fiscal Year is required for submission" }]}
              >
                <InputNumber min={2020} max={2100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: "Please enter request title" }]}
              >
                <Input
                  placeholder="Enter request title"
                  onChange={() => setTitleManuallyEdited(true)}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: "Please enter request description" }]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Describe the request..."
                  onChange={() => setDescriptionManuallyEdited(true)}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} placeholder="Notes" />
              </Form.Item>
            </Col>

            {/* Hidden form field to store isUnplanned value */}
            <Form.Item name="isUnplanned" style={{ display: "none" }}>
              <Input />
            </Form.Item>
          </Row>
        </Card>

        <Card
          title="Items"
          style={{ marginBottom: 24 }}
          extra={
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                itemForm.resetFields();
                setEditingIndex(null);
                setIsItemModalVisible(true);
              }}
            />
          }
        >
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(r) => r.id || `${r.name}-${r.planRef?.itemId || ""}-${r.quantity}-${r.unitPrice}`}
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={8}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>ETB {totalAmount.toLocaleString()}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={4}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Card
          title="Attachments"
          style={{ marginBottom: 24 }}
          extra={
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                Upload
              </Button>
            </Upload>
          }
        >
          <Space wrap>
            {attachments.map((att: Attachment) => (
              <div key={att.fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                <Space>
                  {getMimeTypeIcon(att.mimeType)}
                  <a href={filePreviewUrl(att.fileId)} target="_blank" rel="noreferrer">
                    {att.originalName} ({formatBytes(att.size)})
                  </a>
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveAttachment(att.fileId)}>
                    Remove
                  </Button>
                </Space>
              </div>
            ))}
          </Space>
          {!attachments?.length && <div>No attachments</div>}
          
          {/* Hidden form field to store attachments data */}
          <Form.Item name="attachments" style={{ display: "none" }}>
            <Input />
          </Form.Item>
        </Card>
        <Card style={{ textAlign: "right" }}>
          <Space>
            <Button type="default" onClick={handleSaveDraft} icon={<SaveOutlined />}>
              Save Draft
            </Button>
            <Button type="primary" onClick={handleSubmit} icon={<SendOutlined />}>
              Save and Submit
            </Button>
          </Space>
        </Card>
      </Form>

      <Modal
        title={editingIndex !== null ? "Edit Item" : "Add Item"}
        open={isItemModalVisible}
        onOk={saveItem}
        onCancel={() => {
          setIsItemModalVisible(false);
          itemForm.resetFields();
          setEditingIndex(null);
        }}
        okText={editingIndex !== null ? "Update Item" : "Add Item"}
        width={820}
        destroyOnClose
      >
        <Form form={itemForm} layout="vertical" initialValues={{ categoryType: 'GOODS' }}>
          <Row gutter={16}>
            {!isUnplanned && (
              <Col span={24}>
                <Card
                  size="small"
                  title="Link from Approved Plan Items (optional)"
                  extra={
                    <Space>
                      <Input
                        allowClear
                        placeholder="Search approved items..."
                        prefix={<SearchOutlined />}
                        value={planSearch}
                        onChange={(e) => setPlanSearch(e.target.value)}
                        onPressEnter={() => runApprovedSearch()}
                        style={{ width: 260 }}
                      />
                      <Button onClick={() => runApprovedSearch()} loading={approvedLoading}>
                        Search
                      </Button>
                    </Space>
                  }
                >
                  <Row gutter={8} style={{ marginBottom: 8 }}>
                    <Col span={24}>
                      <div style={{ color: "#888", marginBottom: 8 }}>
                        Select an approved plan item to autofill name, description, unit price and supplier.
                      </div>
                    </Col>
                  </Row>
                  <div style={{ maxHeight: 210, overflow: "auto", border: "1px solid #f0f0f0", borderRadius: 6 }}>
                    <Table
                      size="small"
                      dataSource={approvedItems}
                      rowKey={(r) => `${r.planId}-${r.itemId}`}
                      pagination={false}
                      columns={[
                        { title: "Product", dataIndex: "productName", width: 220 },
                        { title: "Description", dataIndex: "productDescription" },
                        { title: "Quantity", dataIndex: "quantity", width: 80 },
                        { title: "Unit Price", dataIndex: "unitPrice", width: 120, render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}` },
                        { title: "Supplier", dataIndex: "supplier", width: 160 },
                        {
                          title: "Select",
                          width: 90,
                          render: (_: any, r: any) => (
                            <Button
                              type="link"
                              onClick={() => {
                                const prev = itemForm.getFieldsValue();
                                itemForm.setFieldsValue({
                                  name: r.productName,
                                  description: r.productDescription,
                                  categoryType: r.categoryType || 'GOODS',
                                  category: r.productCategory,
                                  unit: r.unit,
                                  unitPrice: r.unitPrice,
                                  supplier: r.supplier,
                                  quantity: r.quantity,
                                  priority: r.priority,
                                  justification: r.justification,
                                  planRef: { planId: r.planId, itemId: r.itemId },
                                });
                                message.success("Plan item applied");
                              }}
                            >
                              Use
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </div>
                  <Form.Item name="planRef" style={{ display: "none" }}>
                    <Input />
                  </Form.Item>
                </Card>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                name="categoryType"
                label="Category Type"
                rules={[{ required: true, message: 'Please select a category type' }]}
              >
                <Radio.Group 
                  disabled={!isUnplanned}
                  onChange={() => itemForm.setFieldValue('category', undefined)}
                >
                  {CATEGORY_TYPE_OPTIONS.map(option => (
                    <Tooltip key={option.value} title={option.description}>
                      <span>
                        <Radio value={option.value} style={{ marginRight: 16 }}>
                          {option.label}
                        </Radio>
                      </span>
                    </Tooltip>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: "Please enter name" }]}>
                <Input 
                  placeholder="Item name" 
                  disabled={!isUnplanned}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select or enter a category' }]}
              >
                <AutoComplete
                  disabled={!isUnplanned}
                  placeholder="Select or type to search category"
                  options={(categoriesData[itemCategoryType || 'GOODS'] || []).map(cat => ({
                    value: cat,
                    label: cat,
                  }))}
                  filterOption={(inputValue, option) =>
                    option!.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input.TextArea 
                  rows={2} 
                  placeholder="Description" 
                  disabled={!isUnplanned}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="quantity" label="Quantity" rules={[{ required: true, message: "Enter quantity" }]}>
                <InputNumber 
                  min={0.0000001} 
                  step={0.1} 
                  style={{ width: "100%" }} 
                  disabled={!isUnplanned}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="unit"
                label="Unit"
                rules={[{ required: true, message: 'Please select unit' }]}
                initialValue="pieces"
              >
                <Select
                  placeholder="Select unit"
                  disabled={!isUnplanned}
                >
                  <Select.Option value="pieces">Pieces</Select.Option>
                  <Select.Option value="liter">Liter</Select.Option>
                  <Select.Option value="kg">Kilogram</Select.Option>
                  <Select.Option value="meter">Meter</Select.Option>
                  <Select.Option value="box">Box</Select.Option>
                  <Select.Option value="pack">Pack</Select.Option>
                  <Select.Option value="set">Set</Select.Option>
                  <Select.Option value="pair">Pair</Select.Option>
                  <Select.Option value="bottle">Bottle</Select.Option>
                  <Select.Option value="roll">Roll</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unitPrice" label="Unit Price" rules={[{ required: true, message: "Enter unit price" }]}>
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  style={{ width: "100%" }} 
                  disabled={!isUnplanned && !planRefValue}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier" label="Supplier">
                <Input 
                  placeholder="Supplier name" 
                  disabled={!isUnplanned && !planRefValue}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select
                  placeholder="Select priority"
                  disabled={!isUnplanned && !planRefValue}
                >
                  <Select.Option value="LOW">Low</Select.Option>
                  <Select.Option value="MEDIUM">Medium</Select.Option>
                  <Select.Option value="HIGH">High</Select.Option>
                  <Select.Option value="URGENT">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="justification" label="Justification">
                <Input.TextArea placeholder="Enter justification for this item" rows={3} disabled={!isUnplanned && !planRefValue} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="tags" label="Tags">
                <Select 
                  mode="tags" 
                  placeholder="Add tags" 
                  disabled={!isUnplanned && !planRefValue}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};